/**
 * /med-card/:cardId — read-only view of a shared Centennial Medication Safety
 * Card, opened via printed QR code. Noindex: private-by-link health data.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ShieldCheck, AlertTriangle } from "lucide-react";
import QRCode from "qrcode";
import { fetchMedShareCard } from "@/lib/api";
import MasterWeeklyCard from "@/components/med/MasterWeeklyCard";
import WalletCard from "@/components/med/WalletCard";
import AppointmentCard from "@/components/med/AppointmentCard";

export default function MedCardView() {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState("loading");
  const [qr, setQr] = useState("");

  useEffect(() => {
    if (status !== "ok") return;
    QRCode.toDataURL(window.location.href, {
      width: 480,
      margin: 1,
      color: { dark: "#1C2B23", light: "#FFFFFF" },
    })
      .then(setQr)
      .catch(() => {});
  }, [status]);

  useEffect(() => {
    document.title = "Shared Medication Safety Card | My Centennial Plan";
    let robots = document.head.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    const prev = robots.getAttribute("content");
    robots.setAttribute("content", "noindex, nofollow");
    return () => {
      if (created) robots.remove();
      else if (prev) robots.setAttribute("content", prev);
    };
  }, []);

  useEffect(() => {
    fetchMedShareCard(cardId)
      .then((d) => {
        setCard(d);
        setStatus("ok");
      })
      .catch(() => setStatus("missing"));
  }, [cardId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center text-sm text-[#5C6B62]">
        Opening medication card…
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <div data-testid="card-not-found" className="max-w-md text-center">
          <AlertTriangle size={28} className="mx-auto text-[#C26747]" />
          <h1 className="text-2xl font-medium text-[#1C2B23] mt-3">Card not found</h1>
          <p className="text-sm text-[#5C6B62] mt-2">
            This medication card may have been removed by its owner, or the link may be incorrect.
          </p>
          <Link
            to="/medication-safety"
            className="mt-5 inline-flex bg-[#2B5339] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#1C2B23]"
          >
            Build a free medication card
          </Link>
        </div>
      </div>
    );
  }

  const profile = { name: card.name, meds: card.meds || [] };

  return (
    <div className="min-h-screen bg-[#F9F8F6] print:bg-white">
      <div className="max-w-6xl mx-auto px-6 py-6 print:px-0">
        <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] inline-flex items-center gap-2">
            <ShieldCheck size={12} /> Centennial Medication Safety Card™ · shared via QR code
          </div>
          <button
            data-testid="print-shared-card"
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#2B5339] text-[#5C6B62]"
          >
            <Printer size={13} /> Print
          </button>
        </div>

        <h1
          data-testid="shared-card-title"
          className="mt-3 text-3xl sm:text-4xl font-medium text-[#1C2B23] print:hidden"
        >
          {card.name}&apos;s medication card
        </h1>
        <p className="text-sm text-[#5C6B62] mt-1 print:hidden">
          Last updated {new Date(card.updated_at).toLocaleDateString()} · read-only copy — to make
          changes, open the card on the owner&apos;s device.
        </p>

        <div className="mt-8 space-y-8 pb-16 print:pb-0">
          <MasterWeeklyCard profile={profile} qr={qr} />
          <WalletCard profile={profile} qr={qr} />
          <AppointmentCard profile={profile} qr={qr} />
        </div>

        <div className="pb-16 print:hidden">
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
            <p className="text-xs text-[#5C6B62] leading-relaxed">
              This card is for medication organization and education only. It does not replace a
              pharmacist, physician, prescription label, or professional medical advice. For a
              suspected poisoning or emergency, call emergency services or Poison Control
              (US: 1-800-222-1222).
            </p>
            <Link
              to="/medication-safety"
              className="mt-4 inline-flex bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#1C2B23]"
            >
              Build a free card for yourself or a loved one
            </Link>
          </div>
        </div>
      </div>
      <style>{`@media print { @page { margin: 0.4in; } body { background: #fff !important; } }`}</style>
    </div>
  );
}
